import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import FeedPost from './FeedPost';
import { getMyPosts, toggleLike, addComment, getPostComments, editComment, deleteComment, generatePostShareUrl, deletePost } from '../../../services/feedServices';
import { toast } from 'react-toastify';

const DefaultProfile = '/images/profile/default-profile.png';

// Move ActionButton outside to prevent recreation on every render
const ActionButton = React.memo(({ icon, count, onClick, isLiked, isLast, buttonStyle, actionButtonStyle }) => {
  const buttonClass = useMemo(() =>
    `border ${isLiked ? 'text-red-500' : 'text-gray-500'} bg-transparent border-gray-300 rounded-lg py-2 px-4 transition-colors hover:bg-gray-50 ${isLast ? '' : 'mr-2.5'}`,
    [isLiked, isLast]
  );

  const buttonStyleMemo = useMemo(() =>
    isLast ? { ...actionButtonStyle, marginRight: 0 } : actionButtonStyle,
    [isLast, actionButtonStyle]
  );

  return (
    <button
      className={buttonClass}
      onClick={onClick}
      style={buttonStyleMemo}
    >
      <div className="flex items-center justify-center">
        <Icon icon={icon} style={{fontSize: '1.2rem'}} />
        {count && <span className="ml-1" style={{fontSize: '0.9rem'}}>{count}</span>}
      </div>
    </button>
  );
});

const MyFeed = () => {
  // Posts state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [postingNewPost, setPostingNewPost] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Comments state
  const [newComment, setNewComment] = useState({});
  const [showComments, setShowComments] = useState({});
  const [postComments, setPostComments] = useState({}); // Store comments for each post
  const [commentsLoading, setCommentsLoading] = useState({}); // Loading state for comments
  const [editingComment, setEditingComment] = useState({}); // Track which comment is being edited
  const [editCommentText, setEditCommentText] = useState({}); // Store edit text for each comment
  const [showFullText, setShowFullText] = useState({}); // Track which posts show full text
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Track delete confirmation modal
  const [postToDelete, setPostToDelete] = useState(null); // Store post ID to delete
  const [deletingPost, setDeletingPost] = useState(false); // Track delete operation state

  const user_id = JSON.parse(localStorage.getItem('user')).id;

  // Load my posts
  const loadMyPosts = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getMyPosts(page, 5);

      if (response.success) {
        const transformedPosts = response.data.posts.map(post => ({
          id: post.id,
          user: {
            name: post.user_name,
            avatar: post.user_avatar || DefaultProfile
          },
          content: post.description,
          media: post.media_url ? {
            type: post.media_type,
            url: post.media_url
          } : null,
          isLiked: post.is_liked_by_user === 1,
          likes: post.likes_count,
          commentsCount: post.comments_count,
          created_at: post.created_at
        }));

        if (append) {
          setPosts(prev => [...prev, ...transformedPosts]);
        } else {
          setPosts(transformedPosts);
          // Store user profile from first post
          if (transformedPosts.length > 0) {
            setUserProfile({
              name: transformedPosts[0].user.name,
              profile_pic_url: transformedPosts[0].user.avatar
            });
          }
        }

        setCurrentPage(page);
        setHasMore(response.data.pagination.has_more);
      } else {
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load initial posts
  useEffect(() => {
    loadMyPosts(1);
  }, [loadMyPosts]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      // Check if user has scrolled to bottom (with 100px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (!loadingMore && hasMore) {
          loadMorePosts();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore]);

  // Button styles
  // Memoized styles to prevent re-renders
  const buttonStyle = useMemo(() => ({
    backgroundColor: 'transparent',
    borderColor: '#dee2e6'
  }), []);

  const actionButtonStyle = useMemo(() => ({
    flex: 1,
    marginRight: '10px',
    ...buttonStyle
  }), [buttonStyle]);

  // Load comments for a specific post
  const loadPostComments = useCallback(async (postId) => {
    try {
      setCommentsLoading(prev => ({ ...prev, [postId]: true }));

      // Load all comments at once by setting a high page size
      const response = await getPostComments(postId, 1, 1000);

      if (response.success) {
        const newComments = response.data.comments.map(comment => ({
          id: comment.id,
          user: comment.user_name,
          user_id: comment.user_id,
          avatar: comment.user_avatar || DefaultProfile,
          text: comment.comment,
          timestamp: new Date(comment.commented_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        }));

        setPostComments(prev => ({
          ...prev,
          [postId]: newComments
        }));
      } else {
        console.error('Failed to load comments');
      }
    } catch (error) {
      console.error('Error loading comments:', error);      
    } finally {
      setCommentsLoading(prev => ({ ...prev, [postId]: false }));
    }
  }, []);

  // Event handlers
  const handleLike = async (postId) => {
    try {
      const response = await toggleLike(postId);
      if (response.success) {
        setPosts(posts.map(post =>
          post.id === postId
            ? {
                ...post,
                isLiked: !post.isLiked,
                likes: post.isLiked ? post.likes - 1 : post.likes + 1
              }
            : post
        ));
      } else {
        console.error('Failed to update like');   
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      console.error('Failed to update like');
    }
  };

  const handleComment = (postId) => {
    const isOpening = !showComments[postId];
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));

    // Load comments when opening comments section for the first time
    if (isOpening && !postComments[postId]) {
      loadPostComments(postId);
    }
  };

  const handleSubmitComment = async (postId) => {
    const commentText = newComment[postId];
    if (!commentText || !commentText.trim()) {
      console.error('Please enter a comment');
      return;
    }

    if (commentText.length > 400) {
      console.error('Comment cannot exceed 400 characters');
      return;
    }

    try {
      const response = await addComment(postId, commentText.trim());
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount + 1 }
            : post
        ));

        // Add the new comment to the comments list
        const newCommentObj = {
          id: response.data.comment.id,
          user: response.data.comment.user_name,
          user_id: response.data.comment.user_id,
          avatar: response.data.comment.user_avatar || DefaultProfile,
          text: response.data.comment.comment,
          timestamp: 'Just now'
        };

        setPostComments(prev => ({
          ...prev,
          [postId]: [newCommentObj, ...(prev[postId] || [])]
        }));

        setNewComment(prev => ({ ...prev, [postId]: '' }));
        // console.log('Comment added successfully');
      } else {
        console.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handlePostSubmit = async (newPost) => {
    // console.log('handlePostSubmit called with:', newPost);
    setPostingNewPost(true);

    // Simulate API delay and then refresh the feed
    setTimeout(async () => {
      try {
        await loadMyPosts(1); // Reload posts to include the new one
        // console.log('Post created successfully!');
      } finally {
        setPostingNewPost(false);
      }
    }, 2000); // 2 second delay
  };

  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      // console.log('Loading more posts...', { currentPage: currentPage + 1, hasMore });
      loadMyPosts(currentPage + 1, true);
    }
  }, [loadMyPosts, loadingMore, hasMore, currentPage]);

  const handleShare = async (post) => {
    try {
      // Generate shareable URL for the post
      const response = await generatePostShareUrl(post.id);

      if (response.success) {
        const shareUrl = response.data.shareUrl;

        // Prepare share data
        const shareData = {
          title: `${post.user.name}'s Post`,
          text: post.content || 'Check out this post!',
          url: shareUrl
        };

        // Check if Web Share API is supported
        if (navigator.share) {
          await navigator.share(shareData);
          // console.log('Shared successfully');
        } else {
          // Fallback for browsers that don't support Web Share API
          // Copy to clipboard
          await navigator.clipboard.writeText(shareUrl);
          // console.log('Post link copied to clipboard!');
        }
      } else {
        console.error('Failed to generate share link');
      }
    } catch (error) {
      console.error('Error sharing post:', error);
      if (error.name !== 'AbortError') {
        console.error('Failed to share post');
      }
    }
  };

  // Render functions
  const renderMedia = (media) => {
    if (!media) return null;

    if (media.type === 'image') {
      return (
        <div className="relative" style={{ width: '100%', maxHeight: '400px', overflow: 'hidden', borderRadius: '8px' }}>
          <img 
            src={media.url} 
            className="w-full" 
            alt="Post media" 
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '400px',
              objectFit: 'contain',
              display: 'block'
            }} 
          />
        </div>
      );
    } else if (media.type === 'video') {
      return (
        <div className="relative" style={{ width: '100%', maxHeight: '400px', overflow: 'hidden', borderRadius: '8px' }}>
          <video 
            className="w-full" 
            controls 
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '400px',
              objectFit: 'contain',
              display: 'block'
            }}
          >
            <source src={media.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }
    return null;
  };

  const renderPostContent = (content, post) => {
    if (!content) return null;

    const hasMedia = post.media && (post.media.type === 'image' || post.media.type === 'video');

    // For text-only posts, show full content
    if (!hasMedia) {
      return (
        <div>
          <p className="mb-2">{content}</p>
        </div>
      );
    }

    // For posts with media, show truncated text with "Show more" option
    const shouldTruncate = content.length > 100;
    const isShowingFull = showFullText[post.id];
    const displayText = isShowingFull ? content : content.substring(0, 100) + (shouldTruncate ? '...' : '');

    return (
      <div>
        <p className="mb-2">
          {displayText}
          {shouldTruncate && (
            <button
              className="p-0 ml-2 text-primary no-underline bg-transparent border-none cursor-pointer hover:underline"
              onClick={() => setShowFullText(prev => ({ ...prev, [post.id]: !isShowingFull }))}
            >
              {isShowingFull ? 'Show less' : 'Show more'}
            </button>
          )}
        </p>
      </div>
    );
  };

  // Memoized comment handlers to prevent re-renders
  const handleCommentChange = useCallback((postId, value) => {
    setNewComment(prev => ({ ...prev, [postId]: value }));
  }, []);

  const handleCommentKeyDown = useCallback((e, postId) => {
    if (e.key === 'Enter') {
      handleSubmitComment(postId);
    }
  }, [handleSubmitComment]);

  const handleCommentSubmitClick = useCallback((postId) => {
    handleSubmitComment(postId);
  }, [handleSubmitComment]);

  const handleEditComment = async (postId, commentId) => {
    const editText = editCommentText[commentId];
    if (!editText || !editText.trim()) {
      console.error('Please enter a comment');
      return;
    }

    if (editText.length > 400) {
      console.error('Comment cannot exceed 400 characters');
      return;
    }

    try {
      const response = await editComment(commentId, editText.trim());
      if (response.success) {
        // Update the comment in the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].map(comment =>
            comment.id === commentId
              ? { ...comment, text: editText.trim() }
              : comment
          )
        }));

        setEditingComment(prev => ({ ...prev, [commentId]: false }));
        setEditCommentText(prev => ({ ...prev, [commentId]: '' }));
        // console.log('Comment updated successfully');
      } else {
        console.error('Failed to update comment');
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const response = await deleteComment(commentId);
      if (response.success) {
        // Update the post's comments count
        setPosts(posts.map(post =>
          post.id === postId
            ? { ...post, commentsCount: post.commentsCount - 1 }
            : post
        ));

        // Remove the comment from the comments list
        setPostComments(prev => ({
          ...prev,
          [postId]: prev[postId].filter(comment => comment.id !== commentId)
        }));

        // console.log('Comment deleted successfully');
      } else {
        console.error('Failed to delete comment');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleEditCommentChange = useCallback((commentId, value) => {
    setEditCommentText(prev => ({ ...prev, [commentId]: value }));
  }, []);

  const handleEditCommentKeyDown = useCallback((e, postId, commentId) => {
    if (e.key === 'Enter') {
      handleEditComment(postId, commentId);
    }
  }, [handleEditComment]);

  // Delete post handlers
  const handleDeletePostClick = useCallback((postId) => {
    setPostToDelete(postId);
    setShowDeleteModal(true);
  }, []);

  const handleDeletePostConfirm = async () => {
    if (!postToDelete) return;

    setDeletingPost(true);
    try {
      const response = await deletePost(postToDelete);
      if (response.success) {
        // Remove the post from the posts array
        setPosts(prevPosts => prevPosts.filter(post => post.id !== postToDelete));
toast.success('Post deleted successfully!', {
  position: "top-center", // Options: top-left, top-right, top-center, bottom-left, bottom-right, bottom-center
});        // console.log('Post deleted successfully');
      } else {
        toast.error(response.data?.error_msg || 'Failed to delete post');
        console.error('Failed to delete post:', response.data?.error_msg);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    } finally {
      setDeletingPost(false);
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const handleDeletePostCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const renderComments = (post) => {
    if (!showComments[post.id]) return null;

    const comments = postComments[post.id] || [];
    const isLoading = commentsLoading[post.id];

    return (
      <div className="border-t border-gray-200 pt-3 mt-3">
        <h6 className="mb-3 font-semibold">Comments ({post.commentsCount || 0})</h6>

        {/* Comment Input */}
        <div className="flex mb-3">
          <img src={userProfile?.profile_pic_url || DefaultProfile} className="rounded-full mr-2" alt="Profile" style={{width: '32px', height: '32px'}} />
          <div className="flex-1">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Write a comment..."
              value={newComment[post.id] || ''}
              onChange={(e) => handleCommentChange(post.id, e.target.value)}
              onKeyDown={(e) => handleCommentKeyDown(e, post.id)}
              maxLength={400}
            />
            <div className="flex justify-end mt-1">
              <small className={(newComment[post.id] || '').length > 360 ? 'text-yellow-500' : 'text-gray-500'}>
                {(newComment[post.id] || '').length}/400 characters
              </small>
            </div>
          </div>
          <button
            className="bg-primary hover:bg-primary/90 text-white font-medium py-1.5 px-3 rounded-lg ml-2 w-auto text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleCommentSubmitClick(post.id)}
            disabled={!newComment[post.id] || !newComment[post.id].trim()}
          >
            <Icon icon="mdi:send" />
          </button>
        </div>

        {/* Comments Loading State */}
        {isLoading ? (
          <div className="text-center py-3">
            <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin inline-block" role="status">
              <span className="sr-only">Loading comments...</span>
            </div>
            <p className="mt-2 text-gray-500 text-sm">Loading comments...</p>
          </div>
        ) : (
          <>
            {/* Comments Container */}
            <div id={`comments-container-${post.id}`}>
              {/* Existing Comments */}
              {comments.map(comment => (
                <div key={comment.id} className="flex mb-2">
                  <img src={comment.avatar} className="rounded-full mr-2" alt={comment.user} style={{width: '32px', height: '32px'}} />
                  <div className="bg-gray-100 rounded-lg p-2 flex-1">
                    <div className="flex justify-between items-start">
                      <div className="font-semibold">{comment.user}</div>
                      {/* Show edit/delete options only for user's own comments */}
                      {comment.user_id === user_id && (
                        <div className="flex gap-1">
                          {editingComment[comment.id] ? (
                            <>
                              <button
                                className="bg-green-500 hover:bg-green-600 text-white font-medium py-1 px-2 rounded text-sm"
                                onClick={() => handleEditComment(post.id, comment.id)}
                              >
                                <Icon icon="mdi:check" style={{fontSize: '0.8rem'}} />
                              </button>
                              <button
                                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-1 px-2 rounded text-sm"
                                onClick={() => {
                                  setEditingComment(prev => ({ ...prev, [comment.id]: false }));
                                  setEditCommentText(prev => ({ ...prev, [comment.id]: '' }));
                                }}
                              >
                                <Icon icon="mdi:close" style={{fontSize: '0.8rem'}} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="border border-primary text-primary hover:bg-primary hover:text-white font-medium py-1 px-2 rounded text-sm"
                                onClick={() => {
                                  setEditingComment(prev => ({ ...prev, [comment.id]: true }));
                                  setEditCommentText(prev => ({ ...prev, [comment.id]: comment.text }));
                                }}
                                title="Edit comment"
                              >
                                <Icon icon="mdi:pencil" style={{fontSize: '0.8rem'}} />
                              </button>
                              <button
                                className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-medium py-1 px-2 rounded text-sm"
                                onClick={() => handleDeleteComment(post.id, comment.id)}
                                title="Delete comment"
                              >
                                <Icon icon="mdi:delete" style={{fontSize: '0.8rem'}} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {editingComment[comment.id] ? (
                      <div className="mt-2">
                        <input
                          type="text"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          value={editCommentText[comment.id] || ''}
                          onChange={(e) => handleEditCommentChange(comment.id, e.target.value)}
                          onKeyDown={(e) => handleEditCommentKeyDown(e, post.id, comment.id)}
                          maxLength={400}
                          autoFocus
                        />
                        <div className="flex justify-end mt-1">
                          <small className={(editCommentText[comment.id] || '').length > 360 ? 'text-yellow-500' : 'text-gray-500'}>
                            {(editCommentText[comment.id] || '').length}/400 characters
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}>
                        {comment.text}
                      </div>
                    )}
                    
                    <div className="text-gray-500 text-sm mt-1">{comment.timestamp}</div>
                  </div>
                </div>
              ))}


            </div>
          </>
        )}
      </div>
    );
  };



  return (
    <div className="container mx-auto py-4">
      <div className="flex justify-center">
        <div className="w-full md:w-2/3 p-0">
          {/* Feed Info */}
          <div className="text-center mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <p className="text-gray-500 mb-0 text-sm">
                This is your personal loop where you can see all your posts and updates. 
                Create new posts below and manage your content here.
              </p>
            </div>
          </div>

          {/* Profile Header */}
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <div className="flex items-center">
              <div className="text-end mr-3">
                <h5 className="mb-0 text-lg font-semibold">My Posts</h5>
                <small className="text-gray-500">Your personal posts and updates</small>
              </div>
              <img 
                src={userProfile?.profile_pic_url || DefaultProfile} 
                className="rounded-full" 
                alt={userProfile?.name || "Profile"} 
                style={{width: '50px', height: '50px'}} 
              />
            </div>
          </div>

          {/* Create Post Component */}
          <FeedPost onPostSubmit={handlePostSubmit} userProfile={userProfile} />


          {/* New Post Loading State */}
          {postingNewPost && (
            <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-primary mb-4 bg-gray-50 animate-fadeIn">
              <div className="p-4 text-center py-4">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin inline-block mb-3" role="status">
                  <span className="sr-only">Creating post...</span>
                </div>
                <h6 className="text-primary mb-2 font-semibold">Creating your post...</h6>
                <p className="text-gray-500 mb-0">Please wait while we process your content</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin inline-block" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2 text-gray-500">Loading your posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-4">
              <Icon icon="mdi:post-outline" style={{ fontSize: '3rem', color: '#6c757d' }} />
              <p className="mt-2 text-gray-500">No posts yet. Be the first to share something!</p>
            </div>
          ) : (
            <>
              {/* Posts Feed */}
              {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-100 mb-4">
                <div className="p-4">
                  {/* Post Header */}
                  <div className="flex items-center mb-3">
                    <img src={post.user.avatar} className="rounded-full mr-3" alt={post.user.name} style={{width: '40px', height: '40px'}} />
                    <div className="flex-1">
                      <h6 className="mb-0 font-semibold">{post.user.name}</h6>
                      <small className="text-gray-500">{new Date(post.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</small>
                    </div>
                    {/* Delete Button - Only show for user's own posts */}
                    <button
                      className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-medium py-1 px-2 rounded text-sm w-auto"
                      onClick={() => handleDeletePostClick(post.id)}
                      title="Delete post"
                    >
                      <Icon icon="mdi:delete" style={{ fontSize: '1rem' }} />
                    </button>
                  </div>

                  {/* Post Content */}
                  <div className="mb-3">
                    {renderPostContent(post.content, post)}
                    {renderMedia(post.media)}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between">
                    <ActionButton
                      icon={post.isLiked ? "mdi:heart" : "mdi:heart-outline"}
                      count={post.likes}
                      onClick={() => handleLike(post.id)}
                      isLiked={post.isLiked}
                      buttonStyle={buttonStyle}
                      actionButtonStyle={actionButtonStyle}
                    />
                    <ActionButton
                      icon="mdi:comment-outline"
                      count={post.commentsCount || 0}
                      onClick={() => handleComment(post.id)}
                      buttonStyle={buttonStyle}
                      actionButtonStyle={actionButtonStyle}
                    />
                    <ActionButton
                      icon="mdi:share-variant-outline"
                      onClick={() => handleShare(post)}
                      isLast={true}
                      buttonStyle={buttonStyle}
                      actionButtonStyle={actionButtonStyle}
                    />
                  </div>

                  {/* Comments Section */}
                  {renderComments(post)}
                </div>
              </div>
              ))}

              {/* Loading More Posts Indicator */}
              {loadingMore && (
                <div className="text-center py-4">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin inline-block mb-2" role="status">
                    <span className="sr-only">Loading more posts...</span>
                  </div>
                  <p className="text-primary mb-0">Loading more posts...</p>
                </div>
              )}

              {!hasMore && posts.length > 0 && (
                <div className="text-center py-3">
                  <p className="text-gray-500">You've reached the end of your posts!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleDeletePostCancel}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h5 className="text-lg font-semibold flex items-center">
                <Icon icon="mdi:delete" className="mr-2 text-red-500" />
                Delete Post
              </h5>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                onClick={handleDeletePostCancel}
                disabled={deletingPost}
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center mb-3">
                <Icon icon="mdi:alert-circle" className="text-yellow-500 mr-2" style={{ fontSize: '1.5rem' }} />
                <span>Are you sure you want to delete this post?</span>
              </div>
              <p className="text-gray-500 mb-0">
                This action cannot be undone. The post and all its comments will be permanently deleted.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button
                type="button"
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                onClick={handleDeletePostCancel}
                disabled={deletingPost}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50"
                onClick={handleDeletePostConfirm}
                disabled={deletingPost}
              >
                {deletingPost ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" role="status">
                      <span className="sr-only">Deleting...</span>
                    </div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:delete" />
                    Delete Post
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyFeed; 